import React, { useMemo } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const COLORS = [
  "#2563eb",
  "#16a34a",
  "#f59e0b",
  "#dc2626",
  "#7c3aed",
  "#0891b2",
  "#ea580c",
  "#4f46e5",
  "#65a30d",
  "#db2777",
];

const formatCurrency = (value) => {
  const numericValue = Number(value || 0);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(numericValue);
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0].payload;

  return (
    <div className="rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-md">
      <p className="text-sm font-semibold text-gray-800">{data.label}</p>
      <p className="text-sm text-gray-600">{formatCurrency(data.value)}</p>
    </div>
  );
};

export default function PieChartCard({
  processSteps = [],
  title = "Bare Equipment Costs by Processing Step",
}) {
  const chartData = useMemo(() => {
    return (processSteps || [])
      .filter(
        (obj) =>
          obj &&
          obj.meta &&
          typeof obj.meta.step === "string" &&
          obj.meta.step.trim() !== "" &&
          obj.opex !== undefined &&
          !Number.isNaN(Number(obj.opex))
      )
      .map((obj) => ({
        
        label: obj.meta.step,
        value: Number(obj.opex),
      }));
  }, [processSteps]);

  const total = useMemo(() => {
    return chartData.reduce((sum, item) => sum + item.value, 0);
  }, [chartData]);

  if (!chartData.length) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        <p className="mt-3 text-sm text-gray-500">
          No valid processing step data available.
        </p>
      </div>
    );
  }

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm mb-5">

      <div className="mb-6">
        <h2 className="text-2xl font-bold tracking-tight text-slate-900 md:text-2xl">{title}</h2>
        <p className="mt-1 text-sm text-gray-500">
          Distribution of bare equipment costs across processing steps.
        </p>
      </div>

      {/* Centered Pie Chart */}
      <div className="flex justify-center">
        <div style={{"width": "420px", "height":"300px"}} className="">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                dataKey="value"
                nameKey="label"
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={110}
                paddingAngle={3}
                label={({ percent }) => `${(percent * 100).toFixed(1)}%`}
                labelLine={false}
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${entry.label}-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>

              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Table */}
      <div className="mt-8 overflow-hidden rounded-xl border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">

          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">
                Processing Step
              </th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">
                Bare Equipment Costs
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-200 bg-white">
            {chartData.map((row, index) => (
              <tr key={`${row.label}-${index}`}>
                <td className="px-4 py-3 text-gray-800">
                  <div className="flex items-center gap-3">
                    <span
                      className="h-3 w-3 rounded-full"
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    />
                    <span>{row.label}</span>
                  </div>
                </td>

                <td className="px-4 py-3 font-medium text-gray-700">
                  {formatCurrency(row.value)}
                </td>
              </tr>
            ))}

            <tr className="bg-gray-50">
              <td className="px-4 py-3 font-semibold text-gray-900">Total</td>
              <td className="px-4 py-3 font-semibold text-gray-900">
                {formatCurrency(total)}
              </td>
            </tr>
          </tbody>

        </table>
      </div>

    </section>
  );
}